import {
  DeleteIcon,
  Dialog,
  EditIcon,
  EmptyState,
  List,
  ListItem,
  ListItemText,
  ResourceMenuActions,
  Stack,
  Switch,
  Typography,
} from '@all-manager/design-system-v2';
import ConfirmDeleteDialog from '../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import { QuoteCompaisonColumnDatabase } from '../../../../types';
import { COLUMN_TYPE_LABELS } from '../../utils/columnTypes';
import QuoteColumnFormDialog from '../QuoteColumnFormDialog/QuoteColumnFormDialog';
import { useQuoteColumnsDialog } from './useQuoteColumnsDialog';

type Props = {
  groupId: string;
  /** Sorted by order. */
  columns: QuoteCompaisonColumnDatabase[];
  onClose: () => void;
};

export default function QuoteColumnsDialog(props: Props) {
  const hook = useQuoteColumnsDialog(props);
  const lastIndex = props.columns.length - 1;

  return (
    <>
      <Dialog
        open
        onClose={props.onClose}
        maxWidth="sm"
        fullWidth
        title="Critères de comparaison"
        closeLabel="Fermer"
        actions={{
          primaryAction: { label: 'Nouveau critère', onClick: hook.handleOpenCreate },
          secondaryAction: { label: 'Fermer', onClick: props.onClose },
        }}
      >
        <Stack spacing={2}>
          <Typography variant="body2">
            Les critères forment les lignes de la comparaison. Masquez ceux qui ne servent pas sans perdre les valeurs saisies,
            et changez leur ordre depuis le menu de chaque critère.
          </Typography>

          {props.columns.length === 0 ? (
            <EmptyState
              title="Aucun critère"
              subtitle="Ajoutez un critère (prix, délai, garantie…) pour comparer les devis."
            />
          ) : (
            <List disablePadding aria-label="Critères" aria-busy={hook.isReordering}>
              {props.columns.map((column, index) => (
                <ListItem
                  key={column.id}
                  divider={index < lastIndex}
                  disableGutters
                >
                  <ListItemText
                    primary={column.label}
                    secondary={column.visible ? COLUMN_TYPE_LABELS[column.type] : `${COLUMN_TYPE_LABELS[column.type]} · masqué`}
                  />
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Switch
                      checked={!!column.visible}
                      onChange={() => hook.handleToggleVisibility(column)}
                      slotProps={{ input: { 'aria-label': `Afficher « ${column.label} » dans la comparaison` } }}
                    />
                    <ResourceMenuActions
                      size="small"
                      tooltip={`Options du critère ${column.label}`}
                      actions={[
                        { id: 'up', label: 'Monter', onClick: () => hook.handleMove(index, -1), disabled: index === 0 || hook.isReordering },
                        { id: 'down', label: 'Descendre', onClick: () => hook.handleMove(index, 1), disabled: index === lastIndex || hook.isReordering },
                        { type: 'divider', id: 'divider' },
                        { id: 'edit', label: 'Modifier', icon: <EditIcon fontSize="small" />, onClick: () => hook.handleOpenEdit(column) },
                        { id: 'delete', label: 'Supprimer', icon: <DeleteIcon fontSize="small" />, onClick: () => hook.handleOpenDelete(column) },
                      ]}
                    />
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </Dialog>

      {hook.isCreateOpen ? (
        <QuoteColumnFormDialog groupId={props.groupId} nextOrder={hook.nextOrder} onClose={hook.handleCloseCreate} />
      ) : null}

      {hook.columnToEdit ? (
        <QuoteColumnFormDialog
          groupId={props.groupId}
          nextOrder={hook.nextOrder}
          column={hook.columnToEdit}
          onClose={hook.handleCloseEdit}
        />
      ) : null}

      {hook.columnToDelete ? (
        <ConfirmDeleteDialog
          title="Supprimer le critère"
          onConfirm={hook.handleConfirmDelete}
          onClose={hook.handleCloseDelete}
        >
          Supprimer le critère « {hook.columnToDelete.label} » et les valeurs saisies pour chaque devis ?
        </ConfirmDeleteDialog>
      ) : null}
    </>
  );
}
